import React, { useContext, useEffect, useState } from "react"
import { Button, Spinner, Table, Alert } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash, faChartBar } from "@fortawesome/free-solid-svg-icons"
import SwaggerContext from "../../SwaggerContext"
import { invitationService } from "../../services/invitationService"
import ConfirmationModal from "../../components/ConfirmationModal"
import { notifySuccess, notifyError } from "../../actions/notifications"

const List = () => {
  const [patients, setPatients] = useState(null)
  const [error, setError] = useState("")
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteArguments, setDeleteArguments] = useState(null)
  const navigate = useNavigate()
  const swaggerClient = useContext(SwaggerContext)

  useEffect(() => {
    const loadPatients = async () => {
      try {
        if (!swaggerClient?.invitationApi) {
          throw new Error("API no disponible")
        }

        const result = await invitationService.getInvitedUsers(
          swaggerClient.invitationApi
        )

        if (result.success) {
          setPatients(result.data || [])
          setError("")
        } else {
          const errorMsg = result.error || "Error al cargar pacientes"
          setError(errorMsg)
          setPatients([])
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Error desconocido"
        setError(errorMsg)
        setPatients([])
      }
    }

    loadPatients()
  }, [swaggerClient])

  const handleDeletePatient = async (invitationApi, invitationId, patientName) => {
    try {
      const result = await invitationService.cancelInvitation(invitationApi, invitationId)
      
      if (result.success) {
        notifySuccess(`Paciente ${patientName} eliminado correctamente`)
        // Recargar la lista
        const patientsResult = await invitationService.getInvitedUsers(swaggerClient.invitationApi)
        if (patientsResult.success) {
          setPatients(patientsResult.data || [])
        }
      } else {
        notifyError(result.error || "Error al eliminar paciente")
      }
    } catch (err) {
      notifyError("Error inesperado al eliminar paciente")
    }
  }

  if (patients === null) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "calc(100vh - 200px)" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div>
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {patients.length === 0 ? (
        <Alert variant="info">
          No tienes pacientes vinculados aún. Envía una invitación a través del 
          <strong> Gestor de Invitaciones</strong> para comenzar.
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Nombre</th>
                <th>Apellido</th>
                <th>Usuario</th>
                <th>Email</th>
                <th>Fecha Aceptación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {patients.map((patient, i) => (
                <tr key={patient.userId}>
                  <td>{i + 1}</td>
                  <td>{patient.name}</td>
                  <td>{patient.surname}</td>
                  <td>{patient.username}</td>
                  <td>{patient.email}</td>
                  <td>
                    {new Date(patient.acceptedAt).toLocaleDateString("es-ES")}
                  </td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/mis-pacientes/ver/${patient.userId}`)}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faChartBar} /> Ver Estadísticas
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!patient.invitationId) {
                          notifyError("Este paciente no tiene ID de invitación. Por favor, contacta al administrador para actualizar el backend.")
                          return
                        }
                        setShowDeleteModal(true)
                        setDeleteArguments([
                          swaggerClient.invitationApi,
                          patient.invitationId,
                          `${patient.name} ${patient.surname}`
                        ])
                      }}
                      disabled={!patient.invitationId}
                      title={!patient.invitationId ? "El backend necesita incluir invitationId en InvitedUserInfoDto" : "Eliminar paciente"}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      
      <ConfirmationModal
        show={showDeleteModal}
        setShowModal={setShowDeleteModal}
        deleteFunction={handleDeletePatient}
        deleteArguments={deleteArguments}
      />
    </div>
  )
}

export default List
